import React, { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";
import CONFIG from "../lib/api";

const DocumentMetricsPanel = ({
  document,
  isOpen,
  onClose,
  onViewDocument,
}) => {
  const [metricsData, setMetricsData] = useState(null);
  const [fraudAnalysisData, setFraudAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch metrics when panel opens
  useEffect(() => {
    if (isOpen && document) {
      fetchDocumentMetrics(document.id);
      loadFraudAnalysisData(document.id);
    }
  }, [isOpen, document]);

  const formatFileSize = (sizeInMB) => {
    if (!sizeInMB || isNaN(sizeInMB)) return "Unknown";
    return Number(sizeInMB).toFixed(2);
  };

  const fetchDocumentMetrics = async (documentId) => {
    setLoading(true);
    try {
      const baseUrl = CONFIG.baseUrl;
      const response = await fetch(
        baseUrl + `/documents/${documentId}/metrics`
      );
      if (response.ok) {
        const data = await response.json();
        setMetricsData(data.metrics);
      } else {
        console.log("No metrics available for this document");
        setMetricsData(null);
      }
    } catch (error) {
      console.error("Error loading document metrics:", error);
      setMetricsData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadFraudAnalysisData = async (docId) => {
    setLoading(true);
    try {
      const baseUrl = CONFIG.baseUrl;
      const response = await fetch(
        baseUrl + `/documents/${docId}/fraud-analysis`
      );
      if (response.ok) {
        const data = await response.json();
        setFraudAnalysisData(data);
      } else if (response.status === 404) {
        setFraudAnalysisData(null);
      } else {
        // throw new Error(
        //   `Failed to fetch fraud analysis data: ${response.status}`
        // );
      }
    } catch (error) {
      //   console.error("Error loading fraud analysis data:", error);
      //   setErrors((prev) => ({ ...prev, fraud: error.message }));
      //   // Optional local fallback (dev asset)
      //   try {
      //     const fallbackResponse = await fetch(
      //       "/documents/100_pdf/100_analysis.json"
      //     );
      //     if (fallbackResponse.ok) {
      //       const fallbackData = await fallbackResponse.json();
      //       setFraudData(fallbackData);
      //     }
      //   } catch (fallbackError) {
      //     console.error(
      //       "Fallback fraud analysis data also failed:",
      //       fallbackError
      //     );
      //   }
    } finally {
      //setLoading((prev) => ({ ...prev, fraud: false }));
    }
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Panel Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">
            Document Metrics: {document?.filename}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {/* Panel Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-2">Loading metrics...</span>
            </div>
          ) : metricsData ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Policy Information */}
              {metricsData.policy_number && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-3">
                    Policy Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Policy Number:</span>{" "}
                      {metricsData.policy_number}
                    </div>
                    {metricsData.policy_effective_date && (
                      <div>
                        <span className="font-medium">Effective Date:</span>{" "}
                        {new Date(
                          metricsData.policy_effective_date
                        ).toLocaleDateString()}
                      </div>
                    )}
                    {metricsData.policy_expiration_date && (
                      <div>
                        <span className="font-medium">Expiration Date:</span>{" "}
                        {new Date(
                          metricsData.policy_expiration_date
                        ).toLocaleDateString()}
                      </div>
                    )}
                    {metricsData.policy_status && (
                      <div>
                        <span className="font-medium">Status:</span>{" "}
                        {metricsData.policy_status}
                      </div>
                    )}
                    {metricsData.policy_term_months && (
                      <div>
                        <span className="font-medium">Term:</span>{" "}
                        {metricsData.policy_term_months} months
                      </div>
                    )}
                    {metricsData.total_premium_cents && (
                      <div>
                        <span className="font-medium">Premium:</span> $
                        {(metricsData.total_premium_cents / 100).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Insured Information */}
              {metricsData.insured_name && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-900 mb-3">
                    Insured Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Name:</span>{" "}
                      {metricsData.insured_name}
                    </div>
                    {metricsData.insured_address && (
                      <div>
                        <span className="font-medium">Address:</span>{" "}
                        {metricsData.insured_address}
                      </div>
                    )}
                    {metricsData.insured_city && metricsData.insured_state && (
                      <div>
                        <span className="font-medium">City, State:</span>{" "}
                        {metricsData.insured_city}, {metricsData.insured_state}
                      </div>
                    )}
                    {metricsData.insured_zip && (
                      <div>
                        <span className="font-medium">ZIP:</span>{" "}
                        {metricsData.insured_zip}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Vehicle Information */}
              {metricsData.vehicle_count > 0 && (
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-purple-900 mb-3">
                    Vehicle Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Vehicle Count:</span>{" "}
                      {metricsData.vehicle_count}
                    </div>
                    {metricsData.primary_vehicle_year && (
                      <div>
                        <span className="font-medium">Primary Vehicle:</span>{" "}
                        {metricsData.primary_vehicle_year}{" "}
                        {metricsData.primary_vehicle_make}{" "}
                        {metricsData.primary_vehicle_model}
                      </div>
                    )}
                    {metricsData.primary_vehicle_vin && (
                      <div>
                        <span className="font-medium">VIN:</span>{" "}
                        {metricsData.primary_vehicle_vin}
                      </div>
                    )}
                    {metricsData.primary_vehicle_use && (
                      <div>
                        <span className="font-medium">Use:</span>{" "}
                        {metricsData.primary_vehicle_use}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Agent Information */}
              {metricsData.agent_name && (
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-orange-900 mb-3">
                    Agent Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Agent:</span>{" "}
                      {metricsData.agent_name}
                    </div>
                    {metricsData.agent_id && (
                      <div>
                        <span className="font-medium">Agent ID:</span>{" "}
                        {metricsData.agent_id}
                      </div>
                    )}
                    {metricsData.agent_phone && (
                      <div>
                        <span className="font-medium">Phone:</span>{" "}
                        {metricsData.agent_phone}
                      </div>
                    )}
                    {metricsData.carrier_name && (
                      <div>
                        <span className="font-medium">Carrier:</span>{" "}
                        {metricsData.carrier_name}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* PDF Technical Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">
                  PDF Technical Details
                </h3>
                <div className="space-y-2 text-sm">
                  {metricsData.page_count && (
                    <div>
                      <span className="font-medium">Pages:</span>{" "}
                      {metricsData.page_count}
                    </div>
                  )}
                  {metricsData.file_size_mb && (
                    <div>
                      <span className="font-medium">Size:</span>{" "}
                      {formatFileSize(metricsData.file_size_mb)} MB
                    </div>
                  )}
                  {metricsData.document_creator && (
                    <div>
                      <span className="font-medium">Creator:</span>{" "}
                      {metricsData.document_creator}
                    </div>
                  )}
                  {metricsData.document_producer && (
                    <div>
                      <span className="font-medium">Producer:</span>{" "}
                      {metricsData.document_producer}
                    </div>
                  )}
                  {metricsData.creation_date && (
                    <div>
                      <span className="font-medium">Created:</span>{" "}
                      {new Date(metricsData.creation_date).toLocaleString()}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Encrypted:</span>{" "}
                    {metricsData.is_encrypted ? "Yes" : "No"}
                  </div>
                </div>
              </div>

              {/* Fraud Risk Assessment */}
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="font-semibold text-red-900 mb-3">
                  Fraud Risk Assessment
                </h3>
                <div className="space-y-2 text-sm">
                  {metricsData.fraud_risk_level && (
                    <div>
                      <span className="font-medium">Risk Level:</span>
                      <span
                        className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                          metricsData.fraud_risk_level === "HIGH"
                            ? "bg-red-200 text-red-800"
                            : metricsData.fraud_risk_level === "MEDIUM"
                            ? "bg-yellow-200 text-yellow-800"
                            : "bg-green-200 text-green-800"
                        }`}
                      >
                        {metricsData.fraud_risk_level}
                      </span>
                    </div>
                  )}
                  {metricsData.fraud_risk_score && (
                    <div>
                      <span className="font-medium">Risk Score:</span>{" "}
                      {(metricsData.fraud_risk_score * 100).toFixed(1)}%
                    </div>
                  )}
                  {/* Fraud reasons */}
                  {fraudAnalysisData?.fraud_indicators?.flags?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-600 mb-2">
                        Active Flags:
                      </p>
                      <div className="space-y-1">
                        {fraudAnalysisData.fraud_indicators.flags.map(
                          (flag, idx) => (
                            <div
                              key={idx}
                              className="text-xs p-2 bg-yellow-50 border-l-4 border-yellow-400"
                            >
                              <span className="font-medium capitalize">
                                {flag.type}:
                              </span>{" "}
                              {flag.message}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                  {metricsData.fraud_flags_total > 0 && (
                    <div>
                      <span className="font-medium">Total Flags:</span>{" "}
                      {metricsData.fraud_flags_total}
                    </div>
                  )}
                  {metricsData.fraud_flags_critical > 0 && (
                    <div>
                      <span className="font-medium text-red-600">
                        Critical Flags:
                      </span>{" "}
                      {metricsData.fraud_flags_critical}
                    </div>
                  )}
                  {metricsData.template_markers_found && (
                    <div className="text-red-600">
                      <span className="font-medium">
                        ⚠️ Template markers detected
                      </span>
                    </div>
                  )}
                  {metricsData.suspicious_creator && (
                    <div className="text-red-600">
                      <span className="font-medium">
                        ⚠️ Suspicious creator software
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Processing Status */}
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h3 className="font-semibold text-indigo-900 mb-3">
                  Processing Status
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Extraction Data:</span>{" "}
                    {metricsData.has_llm_data
                      ? "✅ Available"
                      : "❌ Not Available"}
                  </div>
                  <div>
                    <span className="font-medium">Fraud Analysis:</span>{" "}
                    {metricsData.has_fraud_data
                      ? "✅ Available"
                      : "❌ Not Available"}
                  </div>
                  {metricsData.extraction_completeness_score && (
                    <div>
                      <span className="font-medium">Completeness:</span>{" "}
                      {(
                        metricsData.extraction_completeness_score * 100
                      ).toFixed(1)}
                      %
                    </div>
                  )}
                  {metricsData.last_updated && (
                    <div>
                      <span className="font-medium">Last Updated:</span>{" "}
                      {new Date(metricsData.last_updated).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No metrics available for this document
            </div>
          )}
        </div>

        {/* Panel Footer */}
        <div className="flex justify-end space-x-2 p-4 border-t bg-gray-50">
          <button
            onClick={() => onViewDocument && onViewDocument(document?.id)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            View Document
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentMetricsPanel;
